import type { BranchRow } from "@/modules/branches/domain/types";
import { branchesApi } from "@/modules/branches/presentation/api/client";
import type { IngredientListRow } from "@/modules/inventory/domain/types";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useIngredientCategoriesLookupQuery } from "../api/ingredient-categories-queries";
import { useIngredientsQuery } from "../api/queries";

const FILTER_ALL = "__all__" as const;

type LotsSearch = {
  branchId?: string;
  itemType?: string;
  itemId?: string;
  ingredientCategoryId?: string;
  sort?: "fefo" | "fifo";
  sortField?: string;
  sortOrder?: "asc" | "desc";
  offset?: number;
  limit?: number;
};

function toParam(v: string): string | undefined {
  return v === FILTER_ALL || !v ? undefined : v;
}

export function LotsFilter() {
  const nav = useNavigate({ from: "/app/inventory/lots" });
  const search = useSearch({ from: "/app/inventory/lots" }) as LotsSearch;

  const { data: branchesData } = useQuery({
    queryKey: ["branches", "list", { limit: 200, offset: 0 }],
    queryFn: () => branchesApi.list({ limit: 200, offset: 0 }),
  });
  const ingredientsQuery = useIngredientsQuery();
  const categoriesLookup = useIngredientCategoriesLookupQuery();

  const branches: BranchRow[] = branchesData?.data ?? [];
  const ingredients: IngredientListRow[] = ingredientsQuery.data ?? [];

  const branchOptions = [
    { value: FILTER_ALL, label: "ທຸກສາຂາ" },
    ...branches.map((b) => ({ value: b.id, label: b.name })),
  ];
  const itemTypeOptions = [
    { value: FILTER_ALL, label: "ທຸກປະເພດ" },
    { value: "INGREDIENT", label: "ວັດຖຸດິບ" },
    { value: "RETAIL_VARIANT", label: "ຕົວແບບສິນຄ້າ" },
  ];
  const ingredientOptions = [
    { value: FILTER_ALL, label: "ທຸກວັດຖຸດິບ" },
    ...ingredients.map((i) => ({ value: i.id, label: i.name })),
  ];
  const categoryOptions = [
    { value: FILTER_ALL, label: "ທຸກຫມວດ" },
    { value: "__uncategorized__", label: "ຍັງບໍ່ຈັດຫມວດ" },
    ...(categoriesLookup.data ?? []).map((c) => ({
      value: c.id,
      label: c.name,
    })),
  ];
  const sortPreset =
    search.sortField === "receivedAt" && search.sortOrder === "asc"
      ? "fifo"
      : "fefo";
  const hasFilter =
    !!search.branchId ||
    !!search.itemType ||
    !!search.itemId ||
    !!search.ingredientCategoryId ||
    sortPreset === "fifo";

  const update = (next: Partial<LotsSearch>) => {
    nav({
      search: {
        ...search,
        ...next,
        offset: 0,
      },
    });
  };

  const clearFilters = () => {
    nav({
      search: {
        offset: search.offset ?? 0,
        limit: search.limit ?? 20,
        sort: "fefo",
        sortField: "expiryDate",
        sortOrder: "asc",
      },
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 pb-2">
      <SimpleSelect
        placeholder="ສາຂາ"
        value={search.branchId ?? FILTER_ALL}
        onValueChange={(v) => update({ branchId: toParam(v ?? "") })}
        options={branchOptions}
        className="w-40"
      />
      <SimpleSelect
        placeholder="ປະເພດ"
        value={search.itemType ?? FILTER_ALL}
        onValueChange={(v) =>
          update({
            itemType: toParam(v ?? "") as LotsSearch["itemType"],
            itemId: undefined,
          })
        }
        options={itemTypeOptions}
        className="w-40"
      />
      <SimpleSelect
        placeholder="ຫມວດວັດຖຸດິບ"
        value={search.ingredientCategoryId ?? FILTER_ALL}
        onValueChange={(v) =>
          update({
            ingredientCategoryId: toParam(v ?? ""),
            itemType:
              v && v !== FILTER_ALL ? "INGREDIENT" : search.itemType,
          })
        }
        options={categoryOptions}
        className="w-44"
      />
      {search.itemType === "INGREDIENT" && (
        <SimpleSelect
          placeholder="ວັດຖຸດິບ"
          value={search.itemId ?? FILTER_ALL}
          onValueChange={(v) => update({ itemId: toParam(v ?? "") })}
          options={ingredientOptions}
          className="w-48"
        />
      )}
      {/* <span className="text-muted-foreground text-sm">ຮຽງຕາມ:</span>
      <Tabs
        value={sortPreset}
        onValueChange={(v) => {
          const preset = (v as "fefo" | "fifo") || "fefo";
          update({
            sort: preset,
            sortField: preset === "fifo" ? "receivedAt" : "expiryDate",
            sortOrder: "asc",
          });
        }}
      >
        <TabsList className="h-9">
          <TabsTrigger value="fefo" className="gap-1.5 text-xs">
            <CalendarClockIcon className="size-3.5" />
            FEFO
          </TabsTrigger>
          <TabsTrigger value="fifo" className="gap-1.5 text-xs">
            <ArrowRightLeftIcon className="size-3.5" />
            FIFO
          </TabsTrigger>
        </TabsList>
      </Tabs> */}
      {hasFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="size-4" />
          ລ້າງຕົວກອງ
        </button>
      )}
    </div>
  );
}
